import {
  MINIMUM_PASSWORD_LENGTH,
  MINIMUM_REGISTRATION_AGE,
  REQUIRED_ACKNOWLEDGEMENTS,
  SEX_OPTIONS,
} from "./authenticationConstants";
import {
  isValidUsPhoneNumber,
  normalizeUsPhone,
} from "../../../services/usPhoneNumber";

/**
 * Client-side shaping and validation for the authentication requests.
 *
 * These checks give immediate feedback; the backend serializer remains the
 * authority and re-validates everything.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const normalizeEmail = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

export const isValidEmail = (value) => EMAIL_PATTERN.test(normalizeEmail(value));

export const normalizePhone = normalizeUsPhone;

export const isValidPhone = isValidUsPhoneNumber;

/** Build an ISO date from the mockup's month / day / year inputs. */
export const composeDateOfBirth = ({ month, day, year }) => {
  const monthText = String(month || "");
  const dayText = String(day || "");
  const yearText = String(year || "");
  if (
    !/^\d{1,2}$/.test(monthText)
    || !/^\d{1,2}$/.test(dayText)
    || !/^\d{4}$/.test(yearText)
  ) {
    return "";
  }
  const monthValue = Number(monthText);
  const dayValue = Number(dayText);
  const yearValue = Number(yearText);
  const padded = (value) => String(value).padStart(2, "0");
  const iso = `${yearValue}-${padded(monthValue)}-${padded(dayValue)}`;
  const parsed = new Date(Date.UTC(yearValue, monthValue - 1, dayValue));
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getUTCFullYear() !== yearValue ||
    parsed.getUTCMonth() + 1 !== monthValue ||
    parsed.getUTCDate() !== dayValue
  ) {
    return "";
  }
  return iso;
};

export const ageOn = (isoDate, today = new Date()) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate || ""));
  if (!match) return null;
  const birthYear = Number(match[1]);
  const birthMonth = Number(match[2]);
  const birthDay = Number(match[3]);
  if (!composeDateOfBirth({
    year: birthYear,
    month: birthMonth,
    day: birthDay,
  })) {
    return null;
  }
  let age = today.getFullYear() - birthYear;
  const beforeBirthday =
    today.getMonth() + 1 < birthMonth ||
    (today.getMonth() + 1 === birthMonth && today.getDate() < birthDay);
  if (beforeBirthday) age -= 1;
  return age;
};

export const dateOfBirthError = (isoDate, today = new Date()) => {
  if (!isoDate) return "Enter a valid date of birth.";
  const age = ageOn(isoDate, today);
  if (age === null || age < 0) return "Enter a valid date of birth.";
  if (age < MINIMUM_REGISTRATION_AGE) {
    return `You must be ${MINIMUM_REGISTRATION_AGE} or older to continue.`;
  }
  return null;
};

export const buildEmailLookupPayload = (email) => ({
  email: normalizeEmail(email),
});

export const buildLoginPayload = ({ email, password }) => ({
  email: normalizeEmail(email),
  password,
});

/**
 * Build the account-creation request. Field names match the backend serializer
 * and the manifest's `authentication.new_patient.required_fields`.
 */
export const buildRegistrationPayload = (form) => ({
  email: normalizeEmail(form.email),
  password: form.password,
  first_name: String(form.firstName || "").trim(),
  last_name: String(form.lastName || "").trim(),
  phone: normalizePhone(form.phone),
  date_of_birth: form.dateOfBirth,
  sex: form.sex,
  terms_accepted: Boolean(form.acknowledged),
  privacy_accepted: Boolean(form.acknowledged),
  telehealth_consent_accepted: Boolean(form.acknowledged),
});

/** Return field-keyed errors for the registration form, or `{}` when valid. */
export const validateRegistration = (form, today = new Date()) => {
  const errors = {};
  if (!String(form.firstName || "").trim()) {
    errors.firstName = "Enter your first name.";
  }
  if (!String(form.lastName || "").trim()) {
    errors.lastName = "Enter your last name.";
  }
  if (!isValidEmail(form.email)) {
    errors.email = "Enter a valid email address.";
  }
  if (!isValidPhone(form.phone)) {
    errors.phone = "Enter a valid US phone number, including area code.";
  }
  const dobError = dateOfBirthError(form.dateOfBirth, today);
  if (dobError) errors.dateOfBirth = dobError;
  if (!SEX_OPTIONS.some((option) => option.value === form.sex)) {
    errors.sex = "Select the sex you were assigned at birth.";
  }
  if (String(form.password || "").length < MINIMUM_PASSWORD_LENGTH) {
    errors.password = `Use at least ${MINIMUM_PASSWORD_LENGTH} characters.`;
  }
  if (!form.confirmPassword) {
    errors.confirmPassword = "Enter your password again.";
  } else if (form.confirmPassword !== form.password) {
    errors.confirmPassword = "Passwords do not match.";
  }
  if (!form.acknowledged) {
    errors.acknowledged =
      "Please agree to the Terms, Privacy Policy, and telehealth consent.";
  }
  return errors;
};

/** Only fields the backend reported missing are sent. */
export const buildProfileCompletionPayload = (missingFields, values) =>
  (missingFields || []).reduce((payload, field) => {
    if (values[field] !== undefined && values[field] !== "") {
      return { ...payload, [field]: values[field] };
    }
    return payload;
  }, {});

export const REGISTRATION_ACKNOWLEDGEMENT_FIELDS = REQUIRED_ACKNOWLEDGEMENTS;
